import { ArrowBackOutlined, EditOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import apiService from "../../service/Api";
import { tokens } from "../../theme";
import type { IBrandModel } from "../../types/equipment";
import AddSetDialog from "./components/AddSetDialog";
import AnimatedTable, { ColumnDef } from "./components/AnimatedTable";

const ROWS_PER_PAGE = 10;

interface GearSet {
  id: string;
  name: string;
  description: string;
  itemCount: number;
  status: string;
  updatedAt: string;
  items: {
    id: number;
    brand_model: number;
    brand_model_name: string;
    quantity: number;
    notes?: string;
  }[];
}

interface GearSetApiItem {
  id: number | string;
  name?: string;
  description?: string;
  items_count?: number;
  status?: string;
  updated_at?: string;
  items?: Array<{
    id: number;
    brand_model?: number;
    brand_model_name?: string;
    quantity?: number;
    notes?: string;
  }>;
}

const ListSets: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [gearSets, setGearSets] = useState<GearSet[]>([]);
  const [brandModelList, setBrandModelList] = useState<IBrandModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [filterStatus, setFilterStatus] = useState("All Status");
  const [addSetDialogOpen, setAddSetDialogOpen] = useState(false);
  const [editingSet, setEditingSet] = useState<GearSet | null>(null);

  const fetchGearSets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.get<{ results: GearSetApiItem[] }>(
        "/api/equipments/gearsets/",
      );
      const mapped: GearSet[] = response.data.results.map((item) => ({
        id: item.id.toString(),
        name: item.name || "",
        description: item.description || "",
        itemCount: item.items_count ?? 0,
        status: item.status || "Available",
        updatedAt: item.updated_at || "",
        items: (item.items || []).map((i) => ({
          id: i.id,
          brand_model: i.brand_model ?? i.id,
          brand_model_name: i.brand_model_name || "",
          quantity: i.quantity ?? 1,
          notes: i.notes || "",
        })),
      }));
      setGearSets(mapped);
    } catch {
      setError("Failed to load gear sets. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchBrandModels = useCallback(async () => {
    try {
      const res = await apiService.get<{ results: IBrandModel[] }>(
        "/api/equipments/brand-model/",
      );
      setBrandModelList(res.data.results);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchGearSets();
    fetchBrandModels();
  }, [fetchGearSets, fetchBrandModels]);

  const formatUpdatedAt = (dateStr: string) => {
    if (!dateStr) return "—";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    return `${mins} min${mins !== 1 ? "s" : ""} ago`;
  };

  const statuses = [
    "All Status",
    ...Array.from(new Set(gearSets.map((s) => s.status).filter(Boolean))),
  ];

  const filtered = gearSets.filter(
    (s) => filterStatus === "All Status" || s.status === filterStatus,
  );

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginated = filtered.slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE,
  );

  const columns: ColumnDef<GearSet>[] = [
    {
      key: "name",
      label: "Name",
      cellSx: { fontWeight: 500 },
      render: (row) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            color={theme.palette.text.primary}
          >
            {row.name}
          </Typography>
          <Typography variant="caption" color={theme.palette.text.secondary}>
            {row.description}
          </Typography>
        </Box>
      ),
    },
    {
      key: "items",
      label: "Items",
      render: (row) => (
        <Typography variant="body2" color={theme.palette.text.secondary}>
          {row.itemCount} item{row.itemCount !== 1 ? "s" : ""}
        </Typography>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <Chip
          label={row.status}
          size="small"
          sx={{
            fontSize: 12,
            height: 26,
            backgroundColor: colors.neutral[700],
            color: theme.palette.text.primary,
            fontWeight: 500,
            borderRadius: "16px",
          }}
        />
      ),
    },
    {
      key: "updatedAt",
      label: "Updates",
      render: (row) => (
        <Typography variant="body2" color={theme.palette.text.secondary}>
          {formatUpdatedAt(row.updatedAt)}
        </Typography>
      ),
    },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (row) => (
        <Tooltip title="Edit" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditingSet(row);
              setAddSetDialogOpen(true);
            }}
          >
            <EditOutlined
              sx={{ fontSize: 18, color: theme.palette.text.secondary }}
            />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
          <IconButton
            onClick={() => navigate("/gear-management")}
            sx={{ bgcolor: colors.primary[800] }}
          >
            <ArrowBackOutlined />
          </IconButton>
          <Typography
            variant="h5"
            fontWeight="bold"
            color={theme.palette.text.primary}
          >
            All Sets
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: colors.primary[900],
            borderRadius: "16px",
            p: 4,
            minHeight: "70vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <FormControl size="small">
              <Select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(0);
                }}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  height: 40,
                  borderRadius: "12px",
                  backgroundColor: colors.primary[900],
                  color: theme.palette.text.primary,
                  minWidth: 140,
                  "& .MuiSelect-icon": { color: theme.palette.text.primary },
                }}
              >
                {statuses.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={() => {
                setAddSetDialogOpen(true);
                setEditingSet(null);
              }}
              sx={{
                color: colors.primary[900],
                fontSize: 14,
                fontWeight: 500,
                px: 3,
                height: 40,
                borderRadius: "10px",
              }}
            >
              Add Set
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
              <CircularProgress size={32} sx={{ color: colors.primary[400] }} />
            </Box>
          ) : (
            <AnimatedTable
              columns={columns}
              rows={paginated}
              getRowId={(row) => row.id}
              onRowClick={(row) => navigate(`/gear-management/sets/${row.id}`)}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              emptyMessage="No gear sets found."
            />
          )}
        </Box>
      </motion.div>

      <AddSetDialog
        open={addSetDialogOpen}
        onClose={() => {
          setAddSetDialogOpen(false);
          setEditingSet(null);
        }}
        initialData={editingSet}
        isEdit={!!editingSet}
        onSubmit={async (data) => {
          if (editingSet) {
            await apiService.put(`/api/equipments/gearsets/${editingSet.id}/`, {
              name: data.name,
              description: data.description,
              items: data.items,
            });
          } else {
            await apiService.post("/api/equipments/gearsets/", {
              name: data.name,
              description: data.description,
              items: data.items,
            });
          }

          setAddSetDialogOpen(false);
          setEditingSet(null);
          await fetchGearSets();
        }}
        brandModelList={brandModelList}
        preSelectedIds={[]}
      />
    </Box>
  );
};

export default ListSets;
